import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { Canvas, useThree } from '@react-three/fiber';
import { Line } from "@react-three/drei";
import Grid3D from "./Grid3D";
import Mask3D from "./Mask3D";
import Contours3D from "./Contours3D";
import checkForOverlap from "../overlapCheck.component";
import MastHandler3D from "./MastTypes/MastHandler3D";
import CameraControls from 'camera-controls';
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import DwgLayer from "./dwgLayerRenderer";

CameraControls.install({ THREE });

const CameraControlHandler = ({
    cameraCommand,
    setCameraCommand,
    realWidth,
    realHeight,
    centerX = 0,
    centerZ = 0,
    setCameraDirection,
    savedCameraPosition,
    setSavedCameraPosition
}) => {
    const { camera, gl } = useThree();
    const controlsRef = useRef();
    const directionRef = useRef(new THREE.Vector3());

    useEffect(() => {
        const controls = new CameraControls(camera, gl.domElement);
        controlsRef.current = controls;

        const distance = Math.max(realWidth, realHeight);

        const startX = centerX;
        const startY = distance * 1.2;
        const startZ = centerZ;
        const lookAtY = -distance * 0.1;

        controls.setLookAt(startX, startY, startZ, centerX, lookAtY, centerZ, true);

        controls.minPolarAngle = 0;
        controls.maxPolarAngle = Math.PI;
        controls.minDistance = 0;
        controls.maxDistance = Infinity;
        // Keep dolly feeling continuous even when distance reaches min/max limits.
        controls.infinityDolly = true;
        // Zoom towards cursor rather than always around a fixed target point.
        controls.dollyToCursor = true;
        // More "drone-like" forward movement while dragging vertically.
        controls.verticalDragToForward = true;

        controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
        controls.mouseButtons.right = CameraControls.ACTION.TRUCK;
        controls.mouseButtons.middle = CameraControls.ACTION.DOLLY;
        controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
        controls.truckSpeed = 3;
        controls.dollySpeed = 0.30;

        controls.dampingFactor = 0.15;
        controls.dragToOffset = false;

        return () => controls.dispose();
    }, [camera, gl, realWidth, realHeight, centerX, centerZ]);

    useEffect(() => {
        if (!cameraCommand || !controlsRef.current) return;

        const controls = controlsRef.current;
        const command = cameraCommand;
        const distance = Math.max(realWidth, realHeight) / 2;

        console.log('Executing camera command:', command, 'at center:', { centerX, centerZ });


        const commands = {
            top: new THREE.Vector3(centerX, distance, centerZ - distance),
            bottom: new THREE.Vector3(centerX, distance, centerZ + distance),
            left: new THREE.Vector3(centerX - distance, distance, centerZ),
            right: new THREE.Vector3(centerX + distance, distance, centerZ),
            iso1: new THREE.Vector3(centerX - distance, distance, centerZ - distance),
            iso2: new THREE.Vector3(centerX + distance, distance, centerZ - distance),
            iso3: new THREE.Vector3(centerX - distance, distance, centerZ + distance),
            iso4: new THREE.Vector3(centerX + distance, distance, centerZ + distance),
        };

        if (cameraCommand === 'reset' && savedCameraPosition) {
            const { position } = savedCameraPosition;
            const distance = Math.max(realWidth, realHeight);
            const elevatedY = position.y + distance * 0.2;
            const tiltY = -distance * 0.1;

            controls.setLookAt(
                position.x,
                elevatedY,
                position.z,
                centerX,
                tiltY,
                centerZ,
                true
            );
        } else if (commands[cameraCommand]) {
            const pos = commands[cameraCommand];
            controls.setLookAt(pos.x, pos.y, pos.z, centerX, 0, centerZ, true);
        }

        setCameraCommand(null);
    }, [cameraCommand, setCameraCommand, realWidth, realHeight, savedCameraPosition, centerX, centerZ]);

    useFrame((_, delta) => {
        const controls = controlsRef.current;
        if (!controls || !camera) return;

        controls.update(delta);

        const newDir = new THREE.Vector3();
        camera.getWorldDirection(newDir);

        // Round tiny float errors
        newDir.x = Math.abs(newDir.x) < 1e-3 ? 0 : newDir.x;
        newDir.y = Math.abs(newDir.y) < 1e-3 ? 0 : newDir.y;
        newDir.z = Math.abs(newDir.z) < 1e-3 ? 0 : newDir.z;

        // Get previous direction (stored in ref)
        const prevDir = directionRef.current ?? new THREE.Vector3();

        // Calculate angle difference
        const angleChange = newDir.angleTo(prevDir);

        // Only update if rotation changed more than ~2 degrees
        if (angleChange > THREE.MathUtils.degToRad(2)) {
            // console.log(newDir, "Camera in GridResults3D");
            directionRef.current = newDir.clone();
        }
    });
    return null;
}

function TwoSidedAxes({ size = 500, notchSpacing = 10, notchLength = 3, position = [0, 0, 0] }) {
    const segments = useMemo(() => {
        const lines = [];
        const axes = [
            { color: 0xff0000, dir: new THREE.Vector3(1, 0, 0), perp: new THREE.Vector3(0, 0, 1), bothSides: true },
            { color: 0x00ff00, dir: new THREE.Vector3(0, 1, 0), perp: new THREE.Vector3(1, 0, 0), bothSides: false },
            { color: 0x0000ff, dir: new THREE.Vector3(0, 0, 1), perp: new THREE.Vector3(1, 0, 0), bothSides: true },
        ];

        axes.forEach(({ color, dir, perp, bothSides }, axisIndex) => {
            const drawHalfAxis = (sign) => {
                const sideDir = dir.clone().multiplyScalar(sign);
                lines.push({
                    key: `axis-${axisIndex}-${sign}`,
                    color,
                    points: [
                        [0, 0, 0],
                        [sideDir.x * size, sideDir.y * size, sideDir.z * size],
                    ],
                });

                for (let i = notchSpacing; i + notchLength <= size; i += notchSpacing) {
                    const base = sideDir.clone().multiplyScalar(i);
                    const notchOut1 = base.clone().add(perp.clone().multiplyScalar(notchLength));
                    const notchOut2 = base.clone().add(perp.clone().multiplyScalar(-notchLength));

                    lines.push({
                        key: `notch-${axisIndex}-${sign}-${i}-a`,
                        color,
                        points: [
                            [base.x, base.y, base.z],
                            [notchOut1.x, notchOut1.y, notchOut1.z],
                        ],
                    });
                    lines.push({
                        key: `notch-${axisIndex}-${sign}-${i}-b`,
                        color,
                        points: [
                            [base.x, base.y, base.z],
                            [notchOut2.x, notchOut2.y, notchOut2.z],
                        ],
                    });
                }
            };

            drawHalfAxis(1);
            if (bothSides) drawHalfAxis(-1);
        });

        return lines;
    }, [size, notchSpacing, notchLength]);

    return (
        <group>
            {segments.map((segment) => (
                <Line
                    key={segment.key}
                    points={segment.points}
                    color={segment.color}
                    lineWidth={1}
                    position={position}
                    depthTest={false}
                    depthWrite={false}
                    renderOrder={1000}
                />
            ))}
        </group>
    );
}


const StageCanvas = ({
    image,
    realWidth,
    realHeight,
    grids,
    masks,
    columns,
    cameraCommand,
    setCameraCommand,
    areaResults = [],
    colorList,
    luminaires,
    viewer,
    gridScaleX,
    gridScaleY,
    showAllMasks,
    minResult,
    scaleFactor,
    gridOffsetX = 0,
    gridOffsetY = 0,
    open,
    visualGrid,
    savedCameraPosition,
    setSavedCameraPosition,
    axesOpen, axesLength = 500, axesSpacing = 10,
}) => {
    const containerRef = useRef();
    const [cameraDirection, setCameraDirection] = useState(new THREE.Vector3());

    const cameraDistance = Math.max(realWidth, realHeight) * 1.2;
    const cameraNear = 0.01;
    const cameraFar = Math.max(5000, Math.max(realWidth, realHeight) * 40);
    const [overlapInfo, setOverlapInfo] = useState([]);
    const state = useSelector(state => state.ProjectConfig);

    useEffect(() => {
        const overlapInfo = checkForOverlap(masks);
        setOverlapInfo(overlapInfo);
    }, [masks]);

    useEffect(() => {
        const resizeListener = () => {
            const canvas = document.querySelector('canvas');
            if (canvas && canvas.parentElement) {
                const ctx = canvas.getContext('webgl');
                if (ctx && ctx.drawingBufferWidth) {
                    ctx.viewport(0, 0, ctx.drawingBufferWidth, ctx.drawingBufferHeight);
                }
            }
        };

        window.addEventListener("resize", resizeListener);
        resizeListener();
        return () => window.removeEventListener("resize", resizeListener);
    }, []);

    const [layers, setLayers] = useState([]);
    const baseGrid = grids?.[0];
    const axesCenterX = Number(visualGrid?.x ?? baseGrid?.origin?.x?.start ?? 0)
        + (Number(visualGrid?.width ?? baseGrid?.origin?.x?.length ?? realWidth ?? 0) / 2);
    const axesCenterY = Number(visualGrid?.y ?? baseGrid?.origin?.y?.start ?? 0)
        + (Number(visualGrid?.height ?? baseGrid?.origin?.y?.length ?? realHeight ?? 0) / 2);
    const axesPosition = [axesCenterX, 0.35, -axesCenterY];
    const overlayPosition = [0, 0, 0];
    const overlayRotation = [Math.PI / 2, 0, 0];

    useEffect(() => {
        setLayers([]);
        console.log('Viewer changed, resetting layers', viewer);
        if (!viewer) return;
        const newLayers = Array.from(viewer.layers.values());
        setLayers(newLayers);
    }, [viewer]);



    return (
        <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
            <Canvas
                camera={{
                    position: [0, 0, cameraDistance],
                    fov: 45,
                    near: cameraNear,
                    far: cameraFar,
                }}
            >
                <ambientLight intensity={0.5} />
                <directionalLight position={[5, 10, 7]} intensity={1} />

                {axesOpen && (
                    <TwoSidedAxes
                        size={axesLength}
                        notchSpacing={axesSpacing}
                        notchLength={3}
                        position={axesPosition}
                    />
                )}

                <group
                    position={overlayPosition}
                    rotation={overlayRotation}
                >
                    {grids
                        .filter((g) => (g.type === "Horizontal" || g.type === "Vertical") && g.id === 0)
                        .map((grid, i) => (
                            <Grid3D
                                key={i}
                                grid={grid}
                                areaResults={areaResults}
                                state={state}
                                gridScaleX={gridScaleX}
                                gridScaleY={gridScaleY}
                                minResult={minResult}
                                type={grid.type}
                                camera={cameraDirection}
                                renderOrder={100}
                            />
                        ))}
                </group>

                <DwgLayer
                    layers={layers}
                    uniformScale={scaleFactor}
                    position={[0, 0, 0]}
                    bufferRatio={0.05}
                    scaleFactor={scaleFactor}
                    realWidth={realWidth}
                    realHeight={realHeight}
                    grid={grids[0]}
                    visualGrid={visualGrid}
                    originOffsetX={gridOffsetX}
                    originOffsetY={gridOffsetY}
                    renderOrderBase={200}
                />

                <group
                    position={overlayPosition}
                    rotation={overlayRotation}
                >
                    {grids
                        .filter((g) => (g.type === "Horizontal" || g.type === "Vertical") && g.id !== 0)
                        .map((grid, i) => (
                            <Grid3D
                                key={i}
                                grid={grid}
                                areaResults={areaResults}
                                state={state}
                                gridScaleX={gridScaleX}
                                gridScaleY={gridScaleY}
                                minResult={minResult}
                                type={grid.type}
                                camera={cameraDirection}
                                renderOrder={300}
                            />
                        ))}

                    {showAllMasks && masks?.map((mask, i) => (
                        <Mask3D key={i} mask={mask} overlapInfo={overlapInfo} maskList={masks} gridScaleX={gridScaleX} gridScaleY={gridScaleY} renderOrderBase={400} />
                    ))}

                    {columns?.map((column, i) => (
                        <MastHandler3D key={i} column={column} colorList={colorList} luminaires={luminaires} gridScaleX={gridScaleX} gridScaleY={gridScaleY} />
                    ))}

                    <Contours3D
                        colors={state.contourColors}
                        values={state.contourValues}
                        visible={state.showContours}
                        areaResults={areaResults}
                        grid={grids.filter((grid) => grid.name === "Grid #0")}
                        autoContourValues={state.autoContourValues}
                    />
                </group>

                <CameraControlHandler
                    cameraCommand={cameraCommand}
                    setCameraCommand={setCameraCommand}
                    realWidth={realWidth}
                    realHeight={realHeight}
                    centerX={axesCenterX}
                    centerZ={-axesCenterY}
                    setCameraDirection={setCameraDirection}
                    savedCameraPosition={savedCameraPosition}
                    setSavedCameraPosition={setSavedCameraPosition}
                />

            </Canvas>
        </div>
    );
};

export default StageCanvas;
